import { FC } from 'react';
import { ConfirmationModal } from '@new/common/ui/ConfirmationModal/ConfirmationModal';
import { Select } from '@new/common/ui/Select/Select';
import { useDegreeChoice } from '../../hooks/useDegreeChoice';

const TITLE = 'Which degree do you want to complete?';
const DESCRIPTION =
  "We'll build your course plan for this degree. When you save the plan, you're enrolled in it.";
const TYPE_LABEL = 'Degree type';
const PROGRAM_LABEL = 'Program';

interface DegreeChoiceModalProps {
  isOpen: boolean;
  initialDegreeId: number | null;
  onClose: () => void;
  onConfirm: (degreeId: number) => void;
}

export const DegreeChoiceModal: FC<DegreeChoiceModalProps> = ({
  isOpen,
  initialDegreeId,
  onClose,
  onConfirm,
}) => {
  const choice = useDegreeChoice(initialDegreeId);

  const onGenerate = () => {
    if (choice.selectedProgramId != null) {
      onConfirm(choice.selectedProgramId);
    }
  };

  return (
    <ConfirmationModal
      isOpen={isOpen}
      title={TITLE}
      description={DESCRIPTION}
      confirmText="Generate my plan"
      cancelText="Cancel"
      confirmDisabled={choice.selectedProgramId == null}
      onClose={onClose}
      onConfirm={onGenerate}
    >
      <div className="flex w-screen max-w-full flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="l4 medium text-(--neutral-black-NB900)!">{TYPE_LABEL}</span>
          <Select
            value={choice.selectedType}
            onChange={choice.onTypeChange}
            options={choice.typeOptions}
            placeholder={choice.isLoading ? 'Loading degrees…' : 'Select a degree type…'}
            disabled={choice.isLoading}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="l4 medium text-(--neutral-black-NB900)!">{PROGRAM_LABEL}</span>
          <Select
            value={choice.selectedProgramId != null ? String(choice.selectedProgramId) : ''}
            onChange={choice.onProgramChange}
            options={choice.programOptions}
            placeholder={choice.hint ? 'Not available' : 'Select a program…'}
            disabled={choice.programOptions.length === 0}
          />
          <p className="m-0! min-h-10 [font-size:var(--fs-l5)]! text-(--neutral-black-NB600)!">
            {choice.hint}
          </p>
        </div>
      </div>
    </ConfirmationModal>
  );
};
